import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Schedule, Therapist } from '../types';

export interface ExportOptions {
  format: 'pdf' | 'png';
  orientation: 'portrait' | 'landscape';
  includeColors: boolean;
  includeLegend: boolean;
  paperSize: 'a4' | 'letter' | 'a3';
  title?: string;
  subtitle?: string;
}

class ExportService {
  private defaultOptions: ExportOptions = {
    format: 'pdf',
    orientation: 'landscape',
    includeColors: true,
    includeLegend: false,
    paperSize: 'a3',
    title: 'Wisdom Rehabilitation Centre LLC',
    subtitle: ''
  };

  // Export schedule grid as PDF
  async exportScheduleGrid(
    elementId: string, 
    schedule: Schedule,
    therapists: Therapist[],
    options: Partial<ExportOptions> = {}
  ): Promise<void> {
    const finalOptions = { ...this.defaultOptions, ...options };
    
    try {
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error('Schedule element not found');
      }

      // Store original styles to restore later
      const originalStyles = new Map<HTMLElement, string>();
      
      // Temporarily remove overflow restrictions from the original element and its parents
      this.prepareElementForExport(element, originalStyles);

      // Create a temporary container for export
      const exportContainer = await this.createExportContainer(element, schedule, therapists, finalOptions);
      document.body.appendChild(exportContainer);

      // Wait for fonts and images to load
      await new Promise(resolve => setTimeout(resolve, 200));

      // Generate canvas from the export container with optimized settings for wide tables
      const canvas = await html2canvas(exportContainer, {
        scale: 1.5, // Reduced scale for better performance with wide tables
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: exportContainer.scrollWidth,
        height: exportContainer.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: exportContainer.scrollWidth,
        windowHeight: exportContainer.scrollHeight,
        ignoreElements: (element) => {
          // Ignore any scrollbars or overflow indicators
          const htmlElement = element as HTMLElement;
          return element.classList.contains('scrollbar') || 
                 htmlElement.style.overflow === 'scroll' ||
                 htmlElement.style.overflowX === 'scroll';
        }
      });

      // Clean up temporary container
      document.body.removeChild(exportContainer);
      
      // Restore original styles
      this.restoreOriginalStyles(originalStyles);

      if (finalOptions.format === 'pdf') {
        await this.generatePDF(canvas, schedule, finalOptions);
      } else {
        this.downloadImage(canvas, schedule);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw new Error('Failed to export schedule. Please try again.');
    }
  }

  // Prepare element for export by removing overflow restrictions and fixing sticky headers
  private prepareElementForExport(element: HTMLElement, originalStyles: Map<HTMLElement, string>): void {
    // Store and modify the target element
    originalStyles.set(element, element.style.cssText);
    element.style.overflow = 'visible';
    element.style.overflowX = 'visible';
    element.style.overflowY = 'visible';
    element.style.maxWidth = 'none';
    element.style.width = 'auto';

    // Also handle parent containers that might have overflow restrictions
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
      const computedStyle = window.getComputedStyle(parent);
      if (computedStyle.overflow !== 'visible' || computedStyle.overflowX !== 'visible') {
        originalStyles.set(parent, parent.style.cssText);
        parent.style.overflow = 'visible';
        parent.style.overflowX = 'visible';
        parent.style.overflowY = 'visible';
      }
      parent = parent.parentElement;
    }

    // Handle the table specifically
    const table = element.querySelector('table');
    if (table) {
      originalStyles.set(table, table.style.cssText);
      table.style.width = 'auto';
      table.style.minWidth = 'auto';
      table.style.tableLayout = 'auto';
      
      // Fix sticky headers that cause positioning issues in html2canvas
      const stickyHeaders = table.querySelectorAll('th[style*="position: sticky"], th[style*="position:sticky"]');
      stickyHeaders.forEach(header => {
        const headerElement = header as HTMLElement;
        if (!originalStyles.has(headerElement)) {
          originalStyles.set(headerElement, headerElement.style.cssText);
        }
        headerElement.style.position = 'static';
        headerElement.style.top = 'auto';
        headerElement.style.zIndex = 'auto';
      });
      
      // Also check computed styles for sticky positioning
      const allHeaders = table.querySelectorAll('th');
      allHeaders.forEach(header => {
        const headerElement = header as HTMLElement;
        const computedStyle = window.getComputedStyle(headerElement);
        if (computedStyle.position === 'sticky') {
          if (!originalStyles.has(headerElement)) {
            originalStyles.set(headerElement, headerElement.style.cssText);
          }
          headerElement.style.position = 'static';
          headerElement.style.top = 'auto';
          headerElement.style.zIndex = 'auto';
        }
      });
    }
  }

  // Restore original styles
  private restoreOriginalStyles(originalStyles: Map<HTMLElement, string>): void {
    originalStyles.forEach((cssText, element) => {
      element.style.cssText = cssText;
    });
  }

  // Create a styled container for export
  private async createExportContainer(
    originalElement: HTMLElement,
    schedule: Schedule,
    therapists: Therapist[],
    options: ExportOptions
  ): Promise<HTMLElement> {
    // First, measure the original table to determine required width
    const originalTable = originalElement.querySelector('table') || originalElement;
    const originalWidth = originalTable.scrollWidth;
    
    // Calculate minimum width needed (ensure it's wide enough for the full table)
    const minWidth = Math.max(originalWidth + 40, options.orientation === 'landscape' ? 1200 : 800);
    
    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      top: -10000px;
      left: -10000px;
      background: white;
      padding: 20px;
      font-family: Arial, sans-serif;
      width: ${minWidth}px;
      overflow: visible;
    `;

    // Add header
    const header = this.createHeader(schedule, options);
    container.appendChild(header);

    // Clone and style the schedule grid
    const clonedGrid = originalElement.cloneNode(true) as HTMLElement;
    this.styleClonedGrid(clonedGrid, options, minWidth - 40);
    container.appendChild(clonedGrid);

    // Add legend if requested
    if (options.includeLegend) {
      const legend = this.createLegend(therapists, options);
      container.appendChild(legend);
    }

    // Add footer
    const footer = this.createFooter();
    container.appendChild(footer);

    return container;
  }

  // Create header for export
  private createHeader(schedule: Schedule, options: ExportOptions): HTMLElement {
    const header = document.createElement('div');
    header.style.cssText = `
      text-align: center;
      margin-bottom: 20px;
      border-bottom: 2px solid #3498db;
      padding-bottom: 15px;
    `;

    const title = document.createElement('h1');
    title.textContent = options.title || 'Therapy Schedule';
    title.style.cssText = `
      margin: 0 0 10px 0;
      color: #2c3e50;
      font-size: 24px;
      font-weight: bold;
    `;

    const dateRange = document.createElement('h2');
    dateRange.textContent = `${new Date(schedule.weekStartDate).toLocaleDateString()} - ${new Date(schedule.weekEndDate).toLocaleDateString()}`;
    dateRange.style.cssText = `
      margin: 0 0 5px 0;
      color: #34495e;
      font-size: 18px;
      font-weight: normal;
    `;

    const subtitle = document.createElement('p');
    subtitle.textContent = options.subtitle || `Working Hours: ${schedule.workingHoursStart} - ${schedule.workingHoursEnd} | Session Duration: ${schedule.sessionDuration} minutes`;
    subtitle.style.cssText = `
      margin: 0;
      color: #7f8c8d;
      font-size: 14px;
    `;

    header.appendChild(title);
    header.appendChild(dateRange);
    header.appendChild(subtitle);

    return header;
  }

  // Style the cloned grid for export - simplified approach
  private styleClonedGrid(grid: HTMLElement, options: ExportOptions, containerWidth: number): void {
    // Apply basic export styling
    grid.style.cssText += `
      font-size: 12px;
      width: 100%;
      margin: 20px 0;
      overflow: visible !important;
      max-width: none !important;
    `;

    // Find the table element and ensure it's fully visible
    const table = grid.querySelector('table');
    if (table) {
      table.style.cssText += `
        width: auto !important;
        min-width: 100% !important;
        table-layout: auto !important;
        overflow: visible !important;
      `;
    }

    // Remove any overflow restrictions that might hide content
    const scrollableElements = grid.querySelectorAll('[style*="overflow"]');
    scrollableElements.forEach(element => {
      const el = element as HTMLElement;
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
    });

    // Ensure sticky weekday column is reset so it renders on the left
    const weekdayCells = grid.querySelectorAll('.weekday-cell, .weekday-header, td[style*="position: sticky"], th[style*="position: sticky"]');
    weekdayCells.forEach(element => {
      const el = element as HTMLElement;
      el.style.position = 'static';
      el.style.left = 'auto';
      el.style.right = 'auto';
      el.style.zIndex = 'auto';
    });

    // Ensure colors are preserved if requested
    if (options.includeColors) {
      const coloredCells = grid.querySelectorAll('[style*="background-color"]');
      coloredCells.forEach(cell => {
        const element = cell as HTMLElement;
        const bgColor = element.style.backgroundColor;
        if (bgColor) {
          element.style.backgroundColor = bgColor;
          (element.style as any).webkitPrintColorAdjust = 'exact';
          (element.style as any).colorAdjust = 'exact';
        }
      });
    }

    // Remove any width constraints that might cause issues
    const allElements = grid.querySelectorAll('*');
    allElements.forEach(element => {
      const el = element as HTMLElement;
      if (el.style.maxWidth) {
        el.style.maxWidth = 'none';
      }
    });
  }

  // Create legend for export
  private createLegend(therapists: Therapist[], options: ExportOptions): HTMLElement {
    const legend = document.createElement('div');
    legend.style.cssText = `
      margin-top: 20px;
      padding: 15px;
      border: 1px solid #ddd;
      border-radius: 5px;
      background-color: #f8f9fa;
    `;

    const legendTitle = document.createElement('h3');
    legendTitle.textContent = 'Color Legend';
    legendTitle.style.cssText = `
      margin: 0 0 15px 0;
      color: #2c3e50;
      font-size: 16px;
    `;

    const legendGrid = document.createElement('div');
    legendGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 10px;
    `;

    // Add therapist colors
    therapists.forEach(therapist => {
      const item = document.createElement('div');
      item.style.cssText = `
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
      `;

      const colorBox = document.createElement('div');
      const color = '#3498db'; // Default blue color for all therapists
      colorBox.style.cssText = `
        width: 16px;
        height: 16px;
        background-color: ${color};
        border: 1px solid #ddd;
        border-radius: 3px;
      `;

      const label = document.createElement('span');
      label.textContent = `${therapist.name}${therapist.specialization ? ` (${therapist.specialization})` : ''}`;
      label.style.color = '#2c3e50';

      item.appendChild(colorBox);
      item.appendChild(label);
      legendGrid.appendChild(item);
    });

    // Add activity colors
    const activities = [
      { type: 'break', label: 'Break Time' },
      { type: 'group_activity', label: 'Group Activities' }
    ];

    activities.forEach(activity => {
      const item = document.createElement('div');
      item.style.cssText = `
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
      `;

      const colorBox = document.createElement('div');
      // Default colors for different activity types
      const color = activity.type === 'break' ? '#f39c12' : 
                   activity.type === 'group_activity' ? '#9b59b6' : '#3498db';
      colorBox.style.cssText = `
        width: 16px;
        height: 16px;
        background-color: ${color};
        border: 1px solid #ddd;
        border-radius: 3px;
      `;

      const label = document.createElement('span');
      label.textContent = activity.label;
      label.style.color = '#2c3e50';

      item.appendChild(colorBox);
      item.appendChild(label);
      legendGrid.appendChild(item);
    });

    legend.appendChild(legendTitle);
    legend.appendChild(legendGrid);

    return legend;
  }

  // Create footer for export
  private createFooter(): HTMLElement {
    const footer = document.createElement('div');
    footer.style.cssText = `
      margin-top: 20px;
      padding-top: 15px;
      border-top: 1px solid #ddd;
      text-align: center;
      font-size: 10px;
      color: #7f8c8d;
    `;

    footer.textContent = `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()} | Wisdom Rehabilitation Centre LLC`;

    return footer;
  }

  // Generate PDF from canvas
  private async generatePDF(canvas: HTMLCanvasElement, schedule: Schedule, options: ExportOptions): Promise<void> {
    // For wide tables, use A3 landscape or custom size to ensure readability
    let pdfFormat = options.paperSize;
    let pdfOrientation = options.orientation;
    
    // If the canvas is very wide, automatically use A3 landscape for better readability
    const aspectRatio = canvas.width / canvas.height;
    if (aspectRatio > 2 && pdfFormat === 'a4') {
      pdfFormat = 'a3';
      pdfOrientation = 'landscape';
      console.log('Wide table detected, using A3 landscape for better readability');
    }

    const pdf = new jsPDF({
      orientation: pdfOrientation,
      unit: 'mm',
      format: pdfFormat
    });

    const imgData = canvas.toDataURL('image/png', 0.95); // Slightly compress for better file size
    
    // Calculate dimensions
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    // For very wide tables, prioritize width over height to maintain readability
    const widthRatio = pdfWidth / imgWidth;
    const heightRatio = pdfHeight / imgHeight;
    
    let ratio: number;
    let scaledWidth: number;
    let scaledHeight: number;
    
    // If the table is very wide, prioritize fitting the width
    if (aspectRatio > 1.5) {
      ratio = widthRatio;
      scaledWidth = pdfWidth;
      scaledHeight = imgHeight * ratio;
      
      // If it doesn't fit in height, we'll need to split or scale down
      if (scaledHeight > pdfHeight) {
        ratio = Math.min(widthRatio, heightRatio);
        scaledWidth = imgWidth * ratio;
        scaledHeight = imgHeight * ratio;
      }
    } else {
      // For normal aspect ratios, use the standard approach
      ratio = Math.min(widthRatio, heightRatio);
      scaledWidth = imgWidth * ratio;
      scaledHeight = imgHeight * ratio;
    }
    
    // Center the image (calculated later after margin adjustments)

    // Add margins for very wide tables
    const margin = 5; // 5mm margin
    const maxWidth = pdfWidth - (2 * margin);
    const maxHeight = pdfHeight - (2 * margin);
    
    if (scaledWidth > maxWidth || scaledHeight > maxHeight) {
      const finalRatio = Math.min(maxWidth / scaledWidth, maxHeight / scaledHeight);
      scaledWidth *= finalRatio;
      scaledHeight *= finalRatio;
    }

    const finalX = Math.max(margin, (pdfWidth - scaledWidth) / 2);
    const finalY = Math.max(margin, (pdfHeight - scaledHeight) / 2);

    pdf.addImage(imgData, 'PNG', finalX, finalY, scaledWidth, scaledHeight);

    // Generate filename
    const startDate = new Date(schedule.weekStartDate).toLocaleDateString().replace(/\//g, '-');
    const endDate = new Date(schedule.weekEndDate).toLocaleDateString().replace(/\//g, '-');
    const filename = `therapy-schedule-${startDate}-to-${endDate}.pdf`;

    pdf.save(filename);
  }

  // Download image
  private downloadImage(canvas: HTMLCanvasElement, schedule: Schedule): void {
    const link = document.createElement('a');
    const startDate = new Date(schedule.weekStartDate).toLocaleDateString().replace(/\//g, '-');
    const endDate = new Date(schedule.weekEndDate).toLocaleDateString().replace(/\//g, '-');
    
    link.download = `therapy-schedule-${startDate}-to-${endDate}.png`;
    link.href = canvas.toDataURL();
    link.click();
  }

  // Quick export with default options
  async quickExportPDF(elementId: string, schedule: Schedule, therapists: Therapist[]): Promise<void> {
    return this.exportScheduleGrid(elementId, schedule, therapists);
  }

  // Export with custom options
  async customExport(
    elementId: string, 
    schedule: Schedule, 
    therapists: Therapist[], 
    options: Partial<ExportOptions>
  ): Promise<void> {
    return this.exportScheduleGrid(elementId, schedule, therapists, options);
  }
}

export const exportService = new ExportService();
export default exportService;
